import { useEffect, useState, useCallback, useMemo } from "react";
import { ZodError } from "zod";
import { mementoFetch, LoadingStatus, FetcherOptions } from "src/libs/fetcher";
import { useAuth } from "src/hooks/useAuth";

type StartRequest = (
  pathOrUrl: string,
  fetcherOptions?: FetcherOptions
) => void;

function useAuthenticatedFetcherForMutation<Data, Failure>() {
  const { getAccessToken } = useAuth();

  const fetcher = useCallback(
    async (pathOrUrl: string, options: FetcherOptions<any>) => {
      const { response, data } = await mementoFetch({
        getAccessToken,
        pathOrUrl,
        options,
      });

      if (response.ok) {
        return { data: data as Data, status: response.status, ok: response.ok };
      } else {
        return {
          error: data as Failure,
          status: response.status,
          ok: response.ok,
        };
      }
    },
    [getAccessToken]
  );

  return fetcher;
}

function getUnknownError(error: any): Error | ZodError {
  if (error instanceof ZodError) {
    return error;
  } else if (error instanceof Error) {
    return error;
  } else {
    return Error(
      "Something went wrong and this action could not be completed."
    );
  }
}

type ZodErrorResponse = {
  type: "ZodError";
  message: string;
  issues: any;
};

function isZodErrorResponse(obj: any): obj is ZodErrorResponse {
  return (
    obj &&
    typeof obj === "object" &&
    obj !== null &&
    obj.type === "ZodError" &&
    typeof obj.message === "string" &&
    obj.issues !== undefined
  );
}

function getResponseError<FailureType>(
  error: FailureType | ZodErrorResponse
): ZodError | FailureType {
  if (isZodErrorResponse(error)) {
    return new ZodError(error.issues);
  } else {
    return error;
  }
}

// When an error is returned from the server, it can be a ZodError an Error
// exception or the expected error response. This function tells you if it's the
// expected error response.
export function isBaseError<BaseError>(
  error: ResponseError<BaseError>
): error is BaseError {
  if (error instanceof ZodError) {
    return false;
  } else if (error instanceof Error) {
    return false;
  } else {
    return true;
  }
}

export function isZodError<BaseError>(
  error: ResponseError<BaseError>
): error is ZodError {
  return error instanceof ZodError;
}

type ResponseError<BaseError> = BaseError | Error | ZodError;

type Response<DataType, FailureType> =
  | {
      data: DataType;
      error: null;
      status: LoadingStatus.SUCCESS;
    }
  | {
      data: null;
      error: ResponseError<FailureType>;
      status: LoadingStatus.ERROR;
    }
  | {
      data: null;
      error: null;
      status: LoadingStatus.LOADING;
    }
  | {
      data: null;
      error: null;
      status: LoadingStatus.READY;
    };

type UseAuthenticatedRequestResult<TriggerType, DataType, FailureType> = {
  reset: () => void;
  trigger: TriggerType;
} & Response<DataType, FailureType>;

export type GetTrigger<TriggerType> = (
  startRequest: StartRequest
) => TriggerType;

type HookProps<DataType, FailureType, TriggerType> = {
  onSuccess?: (data: DataType) => void;
  onFailure?: (error: ResponseError<FailureType>) => void;
  // If provided, don't wait for manual call to trigger; trigger immediately
  // when the hook is called. Will only trigger once in this way.
  triggerImmediately?: (trigger: TriggerType) => void;
  // what to do if a previous request is loading and a new request is triggered
  concurrencyBehavior?:
    | never // this is just here because otherwise prettier formats this weirdly
    // Abort the previous request, trigger a new one. This is generally not a
    // good idea since requests aren't guaranteed to abort before the request is
    // processed, which can lead to unexpected behavior.
    | "abort"
    // (default) Return, don't trigger a new request.
    | "singleton";
};

export function createAuthenticatedMutationHook<
  TriggerType,
  DataType = any,
  FailureType = any,
>(
  getTrigger: GetTrigger<TriggerType>
): (
  hooksProps?: HookProps<DataType, FailureType, TriggerType>
) => UseAuthenticatedRequestResult<TriggerType, DataType, FailureType> {
  return (hookProps) => {
    const authenticatedFetcher = useAuthenticatedFetcherForMutation<
      DataType,
      FailureType
    >();

    // combine state so that we don't cause multiple re-renders when updating
    // multiple values at once
    const [state, setState] = useState<
      Response<DataType, FailureType> & {
        abortController?: AbortController;
      }
    >({
      status: LoadingStatus.READY,
      data: null,
      error: null,
      abortController: undefined,
    });

    const reset = useCallback(() => {
      if (state.status === LoadingStatus.LOADING) {
        return;
      }

      setState({
        status: LoadingStatus.READY,
        data: null,
        error: null,
      });
    }, [state.status]);

    const startRequest: StartRequest = useCallback(
      async (
        pathOrUrl,
        { method = "GET", headers = {}, body = undefined }: FetcherOptions = {}
      ) => {
        if (state.status === LoadingStatus.LOADING) {
          if (
            hookProps?.concurrencyBehavior === "abort" &&
            state.abortController
          ) {
            state.abortController.abort();
          } else {
            return;
          }
        }

        const abortController = new AbortController();

        setState({
          status: LoadingStatus.LOADING,
          data: null,
          error: null,
          abortController,
        });

        try {
          const { data, error, ok } = await authenticatedFetcher(pathOrUrl, {
            method,
            headers,
            body,
            signal: abortController.signal,
          });

          if (ok) {
            setState({
              status: LoadingStatus.SUCCESS,
              data,
              error: null,
            });
          } else {
            setState({
              status: LoadingStatus.ERROR,
              data: null,
              error: getResponseError<FailureType>(error),
            });
          }
        } catch (err) {
          const error = getUnknownError(err);

          setState({
            status: LoadingStatus.ERROR,
            data: null,
            error,
          });
        }
      },
      [
        authenticatedFetcher,
        state.status,
        state.abortController,
        hookProps?.concurrencyBehavior,
      ]
    );

    const trigger = useMemo(() => getTrigger(startRequest), [startRequest]);

    // put this in a useState so it will only be called on, on the initial load
    useState(() => {
      if (hookProps?.triggerImmediately) {
        hookProps.triggerImmediately(trigger);
      }
    });

    useEffect(() => {
      if (state.status === LoadingStatus.SUCCESS && hookProps?.onSuccess) {
        hookProps.onSuccess(state.data);
      } else if (state.status === LoadingStatus.ERROR && hookProps?.onFailure) {
        hookProps.onFailure(state.error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.status]);

    const result = useMemo(() => {
      const {
        // we don't want to return abortController in the result
        abortController, // eslint-disable-line @typescript-eslint/no-unused-vars
        ...resultState
      } = state;

      return {
        ...resultState,
        reset,
        trigger,
      };
    }, [state, reset, trigger]);

    return result;
  };
}
